import { useQueryClient } from "@tanstack/react-query";
import { Link, useRouter } from "@tanstack/react-router";

import { colors } from "@/assets/colors";
import Warning from "@/assets/openmoji/Warning";
import { useLogoutMutation } from "@/features/auth/api/useLogoutMutation";
import * as Sentry from "@sentry/react";
import { commonComponents as cc } from "../common";
import Description from "../common/Description";
import { ui } from "../ui";

interface Props {
	title?: string;
	error: Error;
	reset: () => void;
}

const ErrorPage = (props: Props) => {
	Sentry.captureException(props);
	const router = useRouter();
	const queryClient = useQueryClient();
	const logout = useLogoutMutation();

	const handleRetry = () => {
		// Reset all queries to ensure fresh data
		queryClient.resetQueries();
		props.reset();
		router.invalidate();
	};

	return (
		<section className="mx-auto p-6 max-w-2xl">
			<cc.Title words={props.title ?? "Something went wrong"} />
			<Warning fill={colors["amber-400"]} className="flex mx-auto w-32 h-32" />
			<div className="flex flex-col justify-center items-center gap-6 mx-auto p-4 w-full text-center">
				<Description className="text-lg">
					{props.error?.message || "An unexpected error occurred"}
				</Description>
				<Description className="text-md text-slate-600">
					We apologize for the inconvenience. You can try refreshing the page or
					logging out and back in.
				</Description>
				<div className="flex flex-col gap-4 w-full max-w-sm">
					<ui.Button
						variant="default"
						size="lg"
						onClick={handleRetry}
						className="w-full"
					>
						Refresh Page & Retry
					</ui.Button>
					<ui.Button variant="outline" size="lg" className="w-full">
						<Link onClick={() => logout.mutate()} to="/">
							Logout & Start Fresh
						</Link>
					</ui.Button>
				</div>
				<Description className="text-slate-500 text-sm">
					If the problem persists, please contact us at{" "}
					<a
						className="text-indigo-600 hover:text-indigo-700"
						href="mailto:support@patient-watch.com"
					>
						support@patient-watch.com
					</a>
				</Description>
			</div>
		</section>
	);
};

export default ErrorPage;
